import React, { useState } from "react";
import {
  useSendCompanyInvitationMutation,
  useGetCompanyInvitationsQuery,
  useResendCompanyInvitationMutation,
  useRevokeCompanyInvitationMutation,
} from "../../../services/api";

export default function EmailInviteSection({ companyId }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("employee");
  const [message, setMessage] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [revokeConfirmId, setRevokeConfirmId] = useState(null);

  const [sendInvitation, { isLoading: isSending, error: sendError }] =
    useSendCompanyInvitationMutation();

  const { data: invitationsData, isLoading: invitationsLoading } =
    useGetCompanyInvitationsQuery(
      { companyId, status: "pending" },
      { skip: !companyId }
    );

  const [resendInvitation, { isLoading: isResending }] =
    useResendCompanyInvitationMutation();

  const [revokeInvitation, { isLoading: isRevoking }] =
    useRevokeCompanyInvitationMutation();

  const pendingInvitations = invitationsData?.invitations || [];

  const handleSend = async (e) => {
    e.preventDefault();
    setSuccessMsg("");

    try {
      await sendInvitation({
        companyId,
        email,
        companyRole: role,
        message: message || undefined,
      }).unwrap();

      setSuccessMsg(`Invitation sent to ${email}`);
      setEmail("");
      setRole("employee");
      setMessage("");
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      console.error("Send invitation failed:", err);
    }
  };

  const handleResend = async (inviteId) => {
    try {
      await resendInvitation({ companyId, inviteId }).unwrap();
    } catch (err) {
      console.error("Resend failed:", err);
    }
  };

  const handleRevoke = async (inviteId) => {
    try {
      await revokeInvitation({ companyId, inviteId }).unwrap();
      setRevokeConfirmId(null);
    } catch (err) {
      console.error("Revoke failed:", err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getErrorMessage = (error) => {
    if (!error) return null;
    return (
      error.data?.error?.message ||
      error.data?.message ||
      "Something went wrong"
    );
  };

  return (
    <section className="team-section">
      <div className="team-section-header">
        <div>
          <h2 className="team-section-title">Email Invitations</h2>
          <p className="team-section-subtitle">
            Send personalized email invitations to join your team
          </p>
        </div>
      </div>

      {/* Send Invite Form */}
      <form onSubmit={handleSend} className="email-invite-form">
        <div className="email-invite-row">
          <div className="email-invite-field email-invite-field-email">
            <label htmlFor="invite-email">Email *</label>
            <input
              type="email"
              id="invite-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@example.com"
              required
            />
          </div>
          <div className="email-invite-field email-invite-field-role">
            <label htmlFor="invite-role">Role</label>
            <select
              id="invite-role"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="employee">Employee</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>

        <div className="email-invite-field">
          <label htmlFor="invite-message">Message (optional)</label>
          <textarea
            id="invite-message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Add a personal message to the invitation..."
            maxLength={500}
            rows={2}
          />
          {message.length > 0 && (
            <span className="email-invite-char-count">
              {message.length}/500
            </span>
          )}
        </div>

        {sendError && (
          <div className="email-invite-error">{getErrorMessage(sendError)}</div>
        )}
        {successMsg && (
          <div className="email-invite-success">{successMsg}</div>
        )}

        <button
          type="submit"
          className="btn-send-invite"
          disabled={isSending || !email}
        >
          {isSending ? "Sending..." : "Send Invitation"}
        </button>
      </form>

      {/* Pending Invitations */}
      {invitationsLoading ? (
        <div className="email-invite-loading">Loading invitations...</div>
      ) : pendingInvitations.length > 0 ? (
        <div className="pending-invitations">
          <h3 className="pending-invitations-title">
            Pending Invitations ({pendingInvitations.length})
          </h3>
          <div className="pending-invitations-list">
            {pendingInvitations.map((invite) => (
              <div key={invite._id} className="pending-invite-item">
                <div className="pending-invite-info">
                  <span className="pending-invite-email">{invite.email}</span>
                  <div className="pending-invite-meta">
                    <span className={`role-badge ${invite.companyRole}`}>
                      {invite.companyRole}
                    </span>
                    <span className="pending-invite-date">
                      Sent {formatDate(invite.createdAt)}
                    </span>
                  </div>
                </div>
                <div className="pending-invite-actions">
                  {revokeConfirmId === invite._id ? (
                    <>
                      <span className="revoke-confirm-text">Revoke?</span>
                      <button
                        className="btn-confirm-revoke"
                        onClick={() => handleRevoke(invite._id)}
                        disabled={isRevoking}
                      >
                        {isRevoking ? "..." : "Yes"}
                      </button>
                      <button
                        className="btn-cancel-revoke"
                        onClick={() => setRevokeConfirmId(null)}
                      >
                        No
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn-resend"
                        onClick={() => handleResend(invite._id)}
                        disabled={isResending}
                      >
                        {isResending ? "..." : "Resend"}
                      </button>
                      <button
                        className="btn-revoke"
                        onClick={() => setRevokeConfirmId(invite._id)}
                      >
                        Revoke
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  );
}
